import { ScoreChart, SectionTitle } from '@sleepstation/components'
import { BreakingLayout, VLayout } from '@sleepstation/components-layouts'
import { CopyRenderer } from '@sleepstation/components-markdown-renderer'
import { DIRECTION, LAYOUT_SPACING } from '@sleepstation/const'
import { parseHtmlToAst } from '@sleepstation/markdown'
import {
  backgroundColor,
  marginH,
  mq,
  paddingBottom,
  paddingTop,
} from '@sleepstation/style-api-helpers'
import { constrain } from '@sleepstation/style-mixins'
import PropTypes from 'prop-types'
import React from 'react'
import styled from 'styled-components'
import { edgesNodesToList } from '../../../../../utils/graphql'
import BreakoutModuleWide from '../../../../shared/breakoutModules/BreakoutModuleWide'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const CHART_COLORS = [`#ad3f26`, `#fa7f28`, `#e7a13d`, `#25904e`]
const CHART_LABELS = [`Very poor`, `Poor`, `OK`, `Good`]

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Description = styled.div.attrs({ 'data-cy': `description` })`
  ${mq(`mediumUp`)} {
    width: 100%;
    max-width: 66%;
  }
`

const Layout = styled(VLayout).attrs({
  spacing: LAYOUT_SPACING.MEDIUM,
  'data-cy': `sleep-efficiency-score-section`,
})`
  ${backgroundColor(`backgroundStart`)}
`

const Inner = styled(VLayout).attrs({ spacing: [0.5, 0.5] })`
  ${paddingTop([2, 2.5])}
`

const Body = styled(BreakingLayout).attrs({
  spacing: [LAYOUT_SPACING.LARGE, LAYOUT_SPACING.X_LARGE],
  direction: [DIRECTION.VERTICAL, DIRECTION.VERTICAL_REVERSED],
})``

const ChartWrapper = styled.div`
  ${constrain([`100%`, 1000, 1280])}
  ${paddingBottom([0, 1])}

  > * {
    ${marginH(0.5)}
  }
`
// This allows the spacing from the container to be carried through to the
// children. Breakout modules will override it otherwise
const BreakoutWrapper = styled.div``

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const SleepEfficencyScoreSection = ({ data }) => {
  const { title, description, score, total, boundaries } = data

  const descriptionHast = parseHtmlToAst(description)
  const flattenedBoundaries = edgesNodesToList(boundaries)

  return (
    <Layout>
      <Inner>
        <BreakoutWrapper>
          <BreakoutModuleWide
            hasBottomBorder={false}
            hasGradient={false}
            paddingV={0}
          >
            <SectionTitle>{title}</SectionTitle>
          </BreakoutModuleWide>
        </BreakoutWrapper>
        <Body>
          <ChartWrapper>
            <ScoreChart
              score={score}
              total={total}
              boundaries={flattenedBoundaries}
              colors={CHART_COLORS}
              labels={CHART_LABELS}
            />
          </ChartWrapper>
          <BreakoutWrapper>
            <BreakoutModuleWide
              hasBottomBorder={false}
              hasGradient={false}
              paddingV={0}
            >
              <Description>
                <CopyRenderer htmlAst={descriptionHast} />
              </Description>
            </BreakoutModuleWide>
          </BreakoutWrapper>
        </Body>
      </Inner>
      <BreakoutModuleWide
        hasBottomBorder
        hasGradient
        paddingV={[
          [0, 1],
          [0, 1],
          [0, 1.5],
        ]}
      />
    </Layout>
  )
}

SleepEfficencyScoreSection.propTypes = {
  data: PropTypes.object.isRequired,
}

SleepEfficencyScoreSection.defaultProps = {}

export default SleepEfficencyScoreSection
